import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchApps, updateStatus, deleteApplication, createManualJobApplication, fetchManualResumes, deleteManualResume, fetchRoleResumes, deleteRoleResume } from '../api';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { triggerCreditRefresh } from '../components/CreditDisplay';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const GenerateResumes = () => {
  const { user, session, getToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [masterInfo, setMasterInfo] = useState(null);
  const [apps, setApps] = useState([]);
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [description, setDescription] = useState('');
  const [jobListingUrl, setJobListingUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [appsLoaded, setAppsLoaded] = useState(false);
  const [manualResumes, setManualResumes] = useState([]);
  const [roleResumes, setRoleResumes] = useState([]);
  const [createManualJobMode, setCreateManualJobMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('All');
  const [currentView, setCurrentView] = useState('list'); // 'list', 'board', 'calendar'

  // Status configuration with colors
  const statusConfig = {
    'Wishlist': { color: '#94a3b8', bgColor: 'rgba(148, 163, 184, 0.2)', borderColor: 'rgba(148, 163, 184, 0.5)' },
    'Applied': { color: '#60a5fa', bgColor: 'rgba(96, 165, 250, 0.2)', borderColor: 'rgba(96, 165, 250, 0.5)' },
    'Phone Screen': { color: '#fbbf24', bgColor: 'rgba(251, 191, 36, 0.2)', borderColor: 'rgba(251, 191, 36, 0.5)' },
    'Interview': { color: '#fbbf24', bgColor: 'rgba(251, 191, 36, 0.2)', borderColor: 'rgba(251, 191, 36, 0.5)' },
    'Offer': { color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.2)', borderColor: 'rgba(34, 197, 94, 0.5)' },
    'Rejected': { color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.2)', borderColor: 'rgba(239, 68, 68, 0.5)' },
    'Accepted': { color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.2)', borderColor: 'rgba(34, 197, 94, 0.5)' }
  };

  const getStatusStyle = (status) => {
    return statusConfig[status] || statusConfig['Wishlist'];
  };

  // Helper function to get filtered items (excludes Generalized Resume - shown in Core Resumes section)
  const getFilteredItems = () => {
    const combinedItems = [
      ...manualResumes
        .filter(r => !(r.resume_name === 'Generalized Resume' || r.is_generalized))
        .map(r => ({ ...r, type: 'manual_resume' })),
      ...apps.map(a => ({ ...a, type: 'job_application' }))
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return combinedItems.filter(item => {
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (item.type === 'manual_resume') {
          if (!item.resume_name?.toLowerCase().includes(query)) return false;
        } else {
          if (!(item.company_name?.toLowerCase().includes(query) ||
                item.job_role?.toLowerCase().includes(query))) return false;
        }
      }

      // Status filter (only for job applications)
      if (statusFilter !== 'All' && item.type === 'job_application') {
        if (item.status !== statusFilter) return false;
      }

      // Date filter
      if (dateFilter !== 'All') {
        const itemDate = new Date(item.created_at);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        switch (dateFilter) {
          case 'Today':
            if (itemDate < today) return false;
            break;
          case 'This Week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            if (itemDate < weekAgo) return false;
            break;
          case 'This Month':
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            if (itemDate < monthStart) return false;
            break;
          case 'Last 3 Months':
            const threeMonthsAgo = new Date(now);
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
            if (itemDate < threeMonthsAgo) return false;
            break;
          case 'Last 6 Months':
            const sixMonthsAgo = new Date(now);
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
            if (itemDate < sixMonthsAgo) return false;
            break;
          default:
            break;
        }
      }

      return true;
    });
  };

  useEffect(() => {
    if (!authLoading && session && user) {
      loadApplications();
      loadMasterResumeInfo();
      loadManualResumes();
      loadRoleResumes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, session, user]);

  const loadMasterResumeInfo = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/resume/master-resume-info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setMasterInfo(data);
    } catch (err) {
      console.error('Failed to load master resume info:', err);
    }
  };

  const loadApplications = async () => {
    const token = getToken();
    console.log('🔍 Loading applications with token:', token ? 'Token exists' : 'No token');

    if (!token) return;

    try {
      console.log('📡 Fetching applications from API...');
      const data = await fetchApps(token);
      console.log('✅ API Response:', data);
      console.log('📊 Type of data:', typeof data, 'Is array?', Array.isArray(data));
      console.log('📈 Data length:', data?.length);

      setApps(data || []);
    } catch (err) {
      console.error('❌ Failed to load applications:', err);
      console.error('Error details:', err.response?.data);
      setApps([]);
    } finally {
      setAppsLoaded(true);
    }
  };

  const loadManualResumes = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const data = await fetchManualResumes(token);
      setManualResumes(data || []);
    } catch (err) {
      console.error('Failed to load manual resumes:', err);
      setManualResumes([]);
    }
  };

  const loadRoleResumes = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const data = await fetchRoleResumes(token);
      setRoleResumes(data || []);
    } catch (err) {
      console.error('Failed to load role resumes:', err);
      setRoleResumes([]);
    }
  };

  const handleDeleteRoleResume = async (role, displayName) => {
    const confirmMessage = `Delete Role Resume?\n\nYou are about to delete:\n"${displayName}"\n\nThis will remove this role-specific resume. You can regenerate it later.\n\nContinue?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteRoleResume(role, getToken());
      await loadRoleResumes();
      alert('Role resume deleted successfully.');
    } catch (err) {
      console.error('Error deleting role resume:', err);
      alert('Failed to delete role resume: ' + err.message);
    }
  };

  const handleCreateApp = async (e) => {
    e.preventDefault();
    
    if (!company || !role || !description) {
      alert('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      console.log('Creating application with:', { company, role, description });
      
      const formData = new FormData();
      formData.append('company', company);
      formData.append('role', role);
      formData.append('description', description);
      if (jobListingUrl) {
        formData.append('job_listing_url', jobListingUrl);
      }

      const response = await fetch(`${API_URL}/resume/create-application`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Server error:', data);
        throw new Error(data.detail || 'Failed to create application');
      }

      console.log('Application created successfully:', data);

      // Refresh credit display to show updated count
      triggerCreditRefresh();

      // Clear form
      setCompany('');
      setRole('');
      setDescription('');
      setJobListingUrl('');

      // Wait a bit for database to update, then reload
      setTimeout(async () => {
        await loadApplications();
        alert('Resume optimized successfully! Check "Your Applications" below.');
      }, 500);
      
    } catch (err) {
      console.error('Error creating application:', err);
      alert(err.message || 'Failed to create optimized resume');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appId, newStatus) => {
    try {
      await updateStatus(appId, newStatus, getToken());
      await loadApplications();
    } catch (err) {
      alert('Failed to update status: ' + err.message);
    }
  };

  const handleDeleteApplication = async (appId, appName) => {
    const confirmMessage = `WARNING: This action is PERMANENT and IRREVERSIBLE!\n\nYou are about to delete:\n"${appName}"\n\nThis will permanently delete:\n• The application record\n• All associated data\n• This data CANNOT be recovered\n\nAre you absolutely sure you want to continue?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Second confirmation for extra safety
    const doubleConfirm = window.confirm('This is your FINAL confirmation.\n\nClick OK to permanently delete this application.\nClick Cancel to keep it.');

    if (!doubleConfirm) {
      return;
    }

    try {
      await deleteApplication(appId, getToken());
      await loadApplications();
      alert('Application deleted successfully.');
    } catch (err) {
      console.error('Error deleting application:', err);
      alert('Failed to delete application: ' + err.message);
    }
  };

  const handleDeleteManualResume = async (resumeId, resumeName) => {
    const confirmMessage = `WARNING: This action is PERMANENT and IRREVERSIBLE!\n\nYou are about to delete:\n"${resumeName}"\n\nThis will permanently delete:\n• The resume record\n• All associated data\n• This data CANNOT be recovered\n\nAre you absolutely sure you want to continue?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Second confirmation for extra safety
    const doubleConfirm = window.confirm('This is your FINAL confirmation.\n\nClick OK to permanently delete this resume.\nClick Cancel to keep it.');

    if (!doubleConfirm) {
      return;
    }

    try {
      await deleteManualResume(resumeId, getToken());
      await loadManualResumes();
      alert('Resume deleted successfully.');
    } catch (err) {
      console.error('Error deleting manual resume:', err);
      alert('Failed to delete resume: ' + err.message);
    }
  };

  const handleCreateManualJobApp = async (e) => {
    e.preventDefault();

    if (!company || !role) {
      alert('Please fill in company name and job role');
      return;
    }

    setLoading(true);
    try {
      const response = await createManualJobApplication(company, role, description, jobListingUrl, getToken());
      console.log('Manual job application created:', response.data);

      // Clear form
      setCompany('');
      setRole('');
      setDescription('');
      setJobListingUrl('');
      setCreateManualJobMode(false);

      // Reload applications
      await loadApplications();
      alert('Manual job application created! Check "Your Applications" below.');
    } catch (err) {
      console.error('Error creating manual job application:', err);
      alert('Failed to create manual job application: ' + err.message);
    } finally {
      setLoading(false);
    }
  };


  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚡</div>
          <p style={{ color: 'var(--text-tertiary)' }}>Loading your workspace...</p>
        </div>
      </div>
    );
  }

  if (!user || !session) {
    navigate('/login');
    return null;
  }

  return (
    <DashboardLayout
      title="Your Resume Workspace"
      subtitle="Generate optimized resumes for every application"
    >

        {/* Create Application Form */}
        {!masterInfo?.has_master ? (
          <div style={{
            marginBottom: '32px',
            padding: '32px',
            background: 'var(--bg-surface)',
            border: '2px dashed rgba(99, 102, 241, 0.3)',
            borderRadius: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
            <h3 style={{ fontSize: '24px', marginBottom: '12px' }}>Create Your Master Resume First</h3>
            <p style={{ color: 'var(--text-tertiary)', marginBottom: '24px' }}>
              Before generating optimized resumes, you need to set up your master resume in your Profile.
            </p>
            <button
              onClick={() => navigate('/profile')}
              style={{
                padding: '14px 32px',
                background: 'var(--gradient-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '700',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
              }}
            >
              Go to Profile
            </button>
          </div>
        ) : (
          <div style={{
            marginBottom: '48px',
            padding: '40px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-light)',
            borderRadius: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '24px' }}>Generate Optimized Resume</h3>
              <button
                type="button"
                onClick={() => setCreateManualJobMode(!createManualJobMode)}
                style={{
                  padding: '10px 20px',
                  background: createManualJobMode ? 'var(--gradient-primary)' : 'rgba(255, 255, 255, 0.05)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
              >
                {createManualJobMode ? '🤖 Switch to AI Mode' : '✍️ Switch to Manual Mode'}
              </button>
            </div>
            <form onSubmit={createManualJobMode ? handleCreateManualJobApp : handleCreateApp}>
              <input
                type="text"
                placeholder="Company Name (e.g., Microsoft)"
                value={company}
                onChange={e => setCompany(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: '16px',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '15px',
                  outline: 'none'
                }}
              />
              <input
                type="text"
                placeholder="Job Role (e.g., Software Engineer - Cloud Infrastructure)"
                value={role}
                onChange={e => setRole(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: '16px',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '15px',
                  outline: 'none'
                }}
              />
              <input
                type="url"
                placeholder="Job Listing URL (optional - e.g., LinkedIn, Indeed link)"
                value={jobListingUrl}
                onChange={e => setJobListingUrl(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: '16px',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '15px',
                  outline: 'none'
                }}
              />
              <textarea
                placeholder={createManualJobMode ? "Job description (optional)" : "Paste the full job description here..."}
                value={description}
                onChange={e => setDescription(e.target.value)}
                required={!createManualJobMode}
                rows={8}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: '24px',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  fontSize: '15px',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  lineHeight: '1.6'
                }}
              />
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '16px 48px',
                  background: loading ? 'rgba(255, 255, 255, 0.1)' : 'var(--gradient-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  transition: 'all 0.2s',
                  boxShadow: loading ? 'none' : '0 4px 12px rgba(99, 102, 241, 0.3)'
                }}
              >
                {loading
                  ? (createManualJobMode ? '⚡ Creating...' : '⚡ Generating...')
                  : (createManualJobMode ? '✍️ Create Manual Resume' : '✨ Generate Resume')
                }
              </button>
            </form>
          </div>
        )}

        {/* Core Resumes Section (Generalized + Role Resumes) */}
        {(roleResumes.length > 0 || manualResumes.some(r => r.resume_name === 'Generalized Resume' || r.is_generalized)) && (
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '8px' }}>
                  Your Core Resumes
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                  Your base resume and role-specific versions. Use these as a starting point for job applications.
                </p>
              </div>
            </div>

            <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))' }}>
              {/* Generalized Resume Card */}
              {manualResumes.filter(r => r.resume_name === 'Generalized Resume' || r.is_generalized).map(generalizedResume => (
                <div
                  key={`generalized-${generalizedResume.id}`}
                  style={{
                    padding: '24px',
                    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                    border: '2px solid rgba(34, 197, 94, 0.4)',
                    borderRadius: '16px',
                    transition: 'all 0.3s',
                    position: 'relative'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)';
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)';
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.4)';
                  }}
                >
                  {/* Status Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '20px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '700',
                    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
                  }}>
                    ⭐ MASTER
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <h4 style={{ fontSize: '18px', fontWeight: '700' }}>
                        Generalized Resume
                      </h4>
                    </div>
                    <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                      Your base resume parsed during onboarding
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => navigate(`/manual-resume/${generalizedResume.id}`)}
                      style={{
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                        color: 'var(--text-primary)',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      View & Edit
                    </button>
                  </div>
                </div>
              ))}
              {roleResumes.map(roleResume => {
                const isStale = roleResume.is_stale;
                const hasOverrides = roleResume.has_overrides;

                return (
                  <div
                    key={roleResume.role}
                    style={{
                      padding: '24px',
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(147, 51, 234, 0.05) 100%)',
                      border: isStale
                        ? '2px dashed rgba(251, 191, 36, 0.5)'
                        : '2px solid rgba(99, 102, 241, 0.4)',
                      borderRadius: '16px',
                      transition: 'all 0.3s',
                      position: 'relative'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(147, 51, 234, 0.08) 100%)';
                      e.currentTarget.style.borderColor = isStale ? 'rgba(251, 191, 36, 0.7)' : 'rgba(99, 102, 241, 0.6)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(147, 51, 234, 0.05) 100%)';
                      e.currentTarget.style.borderColor = isStale ? 'rgba(251, 191, 36, 0.5)' : 'rgba(99, 102, 241, 0.4)';
                    }}
                  >
                    {/* Status Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '20px',
                      background: isStale
                        ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                        : 'linear-gradient(135deg, #6366f1 0%, #9333ea 100%)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      boxShadow: isStale
                        ? '0 2px 8px rgba(251, 191, 36, 0.3)'
                        : '0 2px 8px rgba(99, 102, 241, 0.3)'
                    }}>
                      {isStale ? '⚡ NEEDS UPDATE' : '✓ READY'}
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h4 style={{ fontSize: '18px', fontWeight: '700' }}>
                          {roleResume.display_name || roleResume.role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </h4>
                        {hasOverrides && (
                          <span style={{
                            padding: '3px 8px',
                            background: 'rgba(34, 197, 94, 0.2)',
                            border: '1px solid rgba(34, 197, 94, 0.5)',
                            borderRadius: '6px',
                            fontSize: '10px',
                            fontWeight: '600',
                            color: '#4ade80'
                          }}>
                            CUSTOMIZED
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                        {roleResume.updated_at
                          ? `Last updated: ${new Date(roleResume.updated_at).toLocaleDateString()}`
                          : `Created: ${roleResume.created_at ? new Date(roleResume.created_at).toLocaleDateString() : 'Recently'}`
                        }
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => navigate(`/role-resume/${encodeURIComponent(roleResume.role)}`)}
                        style={{
                          padding: '10px 20px',
                          background: 'var(--gradient-primary)',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        View & Edit
                      </button>
                      <button
                        onClick={() => handleDeleteRoleResume(roleResume.role, roleResume.display_name || roleResume.role)}
                        style={{
                          padding: '10px 16px',
                          background: 'rgba(239, 68, 68, 0.2)',
                          color: 'var(--text-primary)',
                          border: '1px solid rgba(239, 68, 68, 0.5)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Applications List */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <h3 style={{ fontSize: '32px', fontWeight: '800' }}>Your Applications</h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {/* View Switcher */}
              <div style={{ display: 'flex', background: 'var(--bg-elevated)', border: '1px solid var(--border-light)', borderRadius: '10px', padding: '4px' }}>
                <button
                  onClick={() => setCurrentView('list')}
                  style={{
                    padding: '8px 16px',
                    background: currentView === 'list' ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
                    color: currentView === 'list' ? '#fff' : '#a8a8a8',
                    border: currentView === 'list' ? '1px solid rgba(147, 51, 234, 0.5)' : 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  📋 List
                </button>
                <button
                  onClick={() => setCurrentView('board')}
                  style={{
                    padding: '8px 16px',
                    background: currentView === 'board' ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
                    color: currentView === 'board' ? '#fff' : '#a8a8a8',
                    border: currentView === 'board' ? '1px solid rgba(147, 51, 234, 0.5)' : 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  📊 Board
                </button>
                <button
                  onClick={() => setCurrentView('calendar')}
                  style={{
                    padding: '8px 16px',
                    background: currentView === 'calendar' ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
                    color: currentView === 'calendar' ? '#fff' : '#a8a8a8',
                    border: currentView === 'calendar' ? '1px solid rgba(147, 51, 234, 0.5)' : 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  📅 Calendar
                </button>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          {(apps.length > 0 || manualResumes.length > 0) && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="🔍 Search applications by company, role, or resume name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '12px',
                    color: 'var(--text-primary)',
                    fontSize: '15px',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '1px solid rgba(147, 51, 234, 0.5)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value="All">All Statuses</option>
                  <option value="Wishlist">Wishlist</option>
                  <option value="Applied">Applied</option>
                  <option value="Phone Screen">Phone Screen</option>
                  <option value="Interview">Interview</option>
                  <option value="Offer">Offer</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Accepted">Accepted</option>
                </select>

                {/* Date Filter */}
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value="All">All Time</option>
                  <option value="Today">Today</option>
                  <option value="This Week">This Week</option>
                  <option value="This Month">This Month</option>
                  <option value="Last 3 Months">Last 3 Months</option>
                  <option value="Last 6 Months">Last 6 Months</option>
                </select>

                {/* Clear Filters Button */}
                {(statusFilter !== 'All' || dateFilter !== 'All' || searchQuery) && (
                  <button
                    onClick={() => {
                      setStatusFilter('All');
                      setDateFilter('All');
                      setSearchQuery('');
                    }}
                    style={{
                      padding: '10px 20px',
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-light)',
                      borderRadius: '8px',
                      color: 'var(--text-tertiary)',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.color = '#fff';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.target.style.color = '#a8a8a8';
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          )}

          {!appsLoaded ? (
            <p style={{ color: 'var(--text-tertiary)' }}>Loading applications...</p>
          ) : apps.length === 0 && manualResumes.length === 0 ? (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '20px'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>📋</div>
              <p style={{ fontSize: '18px', color: 'var(--text-tertiary)' }}>No applications yet. Create your first one above!</p>
            </div>
          ) : (
            <>
              {/* Render based on current view */}
              {currentView === 'list' && (
                <div style={{ display: 'grid', gap: '20px' }}>
                  {(() => {
                    const filteredItems = getFilteredItems();

                    // Show "no results" message if filtered list is empty
                    if (filteredItems.length === 0) {
                      return (
                        <div style={{
                          padding: '48px',
                          textAlign: 'center',
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: '20px'
                        }}>
                          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔍</div>
                          <p style={{ fontSize: '18px', color: 'var(--text-tertiary)' }}>
                            No applications found matching your filters
                          </p>
                        </div>
                      );
                    }

                    return filteredItems.map(item => item.type === 'manual_resume' ? (
                <div
                  key={`manual-${item.id}`}
                  style={{
                    padding: '24px',
                    background: item.resume_name === 'Generalized Resume' || item.is_generalized
                      ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
                      : 'var(--bg-surface)',
                    border: item.resume_name === 'Generalized Resume' || item.is_generalized
                      ? '2px solid rgba(34, 197, 94, 0.4)'
                      : '1px solid var(--border-light)',
                    borderRadius: '16px',
                    transition: 'all 0.3s',
                    position: 'relative'
                  }}
                  onMouseOver={(e) => {
                    if (item.resume_name === 'Generalized Resume' || item.is_generalized) {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)';
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)';
                    } else {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                      e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (item.resume_name === 'Generalized Resume' || item.is_generalized) {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)';
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.4)';
                    } else {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                >
                  {(item.resume_name === 'Generalized Resume' || item.is_generalized) && (
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '20px',
                      background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
                    }}>
                      ⭐ FROM ONBOARDING
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h4 style={{ fontSize: '20px', fontWeight: '700' }}>
                          {item.resume_name}
                        </h4>
                        {item.resume_name === 'Generalized Resume' || item.is_generalized ? (
                          <span style={{
                            padding: '4px 10px',
                            background: 'rgba(34, 197, 94, 0.2)',
                            border: '1px solid rgba(34, 197, 94, 0.5)',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#4ade80'
                          }}>
                            🎯 MASTER
                          </span>
                        ) : (
                          <span style={{
                            padding: '4px 10px',
                            background: 'rgba(99, 102, 241, 0.2)',
                            border: '1px solid rgba(147, 51, 234, 0.5)',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#a78bfa'
                          }}>
                            ✍️ MANUAL
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                        {item.resume_name === 'Generalized Resume' || item.is_generalized
                          ? 'Your base resume parsed during onboarding'
                          : `Created ${new Date(item.created_at).toLocaleDateString()}`
                        }
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => navigate(`/manual-resume/${item.id}`)}
                      style={{
                        padding: '10px 24px',
                        background: item.resume_name === 'Generalized Resume' || item.is_generalized
                          ? 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)'
                          : 'var(--gradient-primary)',
                        color: 'var(--text-primary)',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {item.resume_name === 'Generalized Resume' || item.is_generalized ? 'View & Edit' : 'Edit Resume'}
                    </button>
                    {!(item.resume_name === 'Generalized Resume' || item.is_generalized) && (
                      <button
                        onClick={() => handleDeleteManualResume(item.id, item.resume_name)}
                        style={{
                          padding: '10px 20px',
                          background: 'rgba(239, 68, 68, 0.2)',
                          color: 'var(--text-primary)',
                          border: '1px solid rgba(239, 68, 68, 0.5)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                /* Job Application */
                <div
                  key={`app-${item.id}`}
                  style={{
                    padding: '24px',
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '16px',
                    transition: 'all 0.3s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                    e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h4 style={{ fontSize: '20px', fontWeight: '700' }}>
                          {item.company_name} — {item.job_role}
                        </h4>
                        {item.is_manual ? (
                          <span style={{
                            padding: '4px 10px',
                            background: 'rgba(99, 102, 241, 0.2)',
                            border: '1px solid rgba(147, 51, 234, 0.5)',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#a78bfa'
                          }}>
                            ✍️ MANUAL
                          </span>
                        ) : (
                          <span style={{
                            padding: '4px 10px',
                            background: 'rgba(34, 197, 94, 0.2)',
                            border: '1px solid rgba(34, 197, 94, 0.5)',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#4ade80'
                          }}>
                            🤖 AI OPTIMIZED
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                        Created {new Date(item.created_at).toLocaleDateString()}
                      </p>
                      {item.job_listing_url && (
                        <a
                          href={item.job_listing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginTop: '8px',
                            fontSize: '13px',
                            color: '#a78bfa',
                            textDecoration: 'none',
                            transition: 'color 0.2s'
                          }}
                          onMouseOver={(e) => e.target.style.color = '#c4b5fd'}
                          onMouseOut={(e) => e.target.style.color = '#a78bfa'}
                        >
                          🔗 View Job Listing
                        </a>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                      <select
                        value={item.status}
                        onChange={e => handleStatusChange(item.id, e.target.value)}
                        style={{
                          padding: '8px 12px',
                          background: getStatusStyle(item.status).bgColor,
                          border: `1px solid ${getStatusStyle(item.status).borderColor}`,
                          borderRadius: '8px',
                          color: getStatusStyle(item.status).color,
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'all 0.2s'
                        }}
                      >
                        <option value="Wishlist">Wishlist</option>
                        <option value="Applied">Applied</option>
                        <option value="Phone Screen">Phone Screen</option>
                        <option value="Interview">Interview</option>
                        <option value="Offer">Offer</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Accepted">Accepted</option>
                      </select>
                      {/* Show timestamp for current status */}
                      {(() => {
                        const statusDateMap = {
                          'Wishlist': item.wishlist_date,
                          'Applied': item.applied_date,
                          'Phone Screen': item.phone_screen_date,
                          'Interview': item.interview_date,
                          'Offer': item.offer_date,
                          'Rejected': item.rejected_date,
                          'Accepted': item.accepted_date
                        };
                        const statusDate = statusDateMap[item.status];
                        return statusDate ? (
                          <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                            {new Date(statusDate).toLocaleDateString()}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => navigate(`/resume/${item.id}`)}
                      style={{
                        padding: '10px 24px',
                        background: 'var(--gradient-primary)',
                        color: 'var(--text-primary)',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      View Resume
                    </button>
                    <button
                      onClick={() => handleDeleteApplication(item.id, `${item.company_name} — ${item.job_role}`)}
                      style={{
                        padding: '10px 20px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        color: 'var(--text-primary)',
                        border: '1px solid rgba(239, 68, 68, 0.5)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ));
              })()}
            </div>
          )}

          {/* Board View (Kanban) */}
          {currentView === 'board' && (
            <div style={{
              width: '100%',
              maxWidth: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              borderRadius: '12px',
              paddingBottom: '12px'
            }}>
              <div style={{
                display: 'flex',
                gap: '16px',
                paddingBottom: '8px',
                paddingRight: '20px',
                minWidth: 'min-content'
              }}>
              {['Wishlist', 'Applied', 'Phone Screen', 'Interview', 'Offer', 'Rejected', 'Accepted'].map(status => {
                const statusApps = apps.filter(app => app.status === status);
                const statusStyle = getStatusStyle(status);

                return (
                  <div
                    key={status}
                    style={{
                      minWidth: '260px',
                      maxWidth: '280px',
                      flexShrink: 0,
                      background: 'var(--bg-surface)',
                      border: `1px solid ${statusStyle.borderColor}`,
                      borderRadius: '12px',
                      padding: '16px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: `2px solid ${statusStyle.borderColor}`
                    }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '700', color: statusStyle.color }}>
                        {status}
                      </h4>
                      <span style={{
                        padding: '4px 10px',
                        background: statusStyle.bgColor,
                        border: `1px solid ${statusStyle.borderColor}`,
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: statusStyle.color
                      }}>
                        {statusApps.length}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {statusApps.map(app => (
                        <div
                          key={app.id}
                          style={{
                            padding: '16px',
                            background: 'var(--bg-elevated)',
                            border: '1px solid var(--border-light)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onClick={() => navigate(`/resume/${app.id}`)}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                            e.currentTarget.style.borderColor = statusStyle.borderColor;
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          }}
                        >
                          <h5 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                            {app.company_name}
                          </h5>
                          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                            {app.job_role}
                          </p>
                          <p style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            {new Date(app.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                      {statusApps.length === 0 && (
                        <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
                          No applications
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          )}

          {/* Calendar View */}
          {currentView === 'calendar' && (
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-light)', borderRadius: '16px', padding: '24px' }}>
              {/* Calendar Header */}
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                <h4 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>
                  {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h4>
                <p style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                  Applications by date
                </p>
              </div>

              {/* Timeline of applications */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {(() => {
                  const filteredItems = getFilteredItems();
                  const jobApps = filteredItems.filter(item => item.type === 'job_application');

                  // Group by date
                  const groupedByDate = jobApps.reduce((groups, app) => {
                    const date = new Date(app.created_at).toLocaleDateString();
                    if (!groups[date]) groups[date] = [];
                    groups[date].push(app);
                    return groups;
                  }, {});

                  const sortedDates = Object.keys(groupedByDate).sort((a, b) =>
                    new Date(b) - new Date(a)
                  );

                  if (sortedDates.length === 0) {
                    return (
                      <div style={{ textAlign: 'center', padding: '48px' }}>
                        <p style={{ fontSize: '18px', color: 'var(--text-tertiary)' }}>
                          No applications to display
                        </p>
                      </div>
                    );
                  }

                  return sortedDates.map(date => (
                    <div key={date} style={{ marginBottom: '24px' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: 'var(--gradient-primary)'
                        }}></div>
                        <h5 style={{ fontSize: '16px', fontWeight: '600' }}>{date}</h5>
                      </div>

                      <div style={{ marginLeft: '24px', borderLeft: '2px solid rgba(99, 102, 241, 0.3)', paddingLeft: '20px' }}>
                        <div style={{ display: 'grid', gap: '12px' }}>
                          {groupedByDate[date].map(app => (
                            <div
                              key={app.id}
                              style={{
                                padding: '16px',
                                background: 'var(--bg-elevated)',
                                border: `1px solid ${getStatusStyle(app.status).borderColor}`,
                                borderLeft: `4px solid ${getStatusStyle(app.status).color}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onClick={() => navigate(`/resume/${app.id}`)}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <div>
                                  <h5 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                                    {app.company_name}
                                  </h5>
                                  <p style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                                    {app.job_role}
                                  </p>
                                </div>
                                <div style={{
                                  padding: '6px 12px',
                                  background: getStatusStyle(app.status).bgColor,
                                  border: `1px solid ${getStatusStyle(app.status).borderColor}`,
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  color: getStatusStyle(app.status).color
                                }}>
                                  {app.status}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
            </>
          )}
        </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>
    </DashboardLayout>
  );
};

export default GenerateResumes;